import { EditButton } from "@/components/WorkoutProfiles/Alerts/AlertList";
import useDrawerPresentation from "@/hooks/useDrawerPresentation";
import { Profile } from "@WahooFitness/cloud-client-types";
import {
  AddIcon,
  BlueSwitch,
  DeleteIcon,
  MenuList,
  MenuListItemType,
  MenuListItemVariant,
  WfIconMiscBikeBellRegular,
  WfIconMiscPlayRegular,
  WfIconNavigationCloseRegular,
} from "@WahooFitness/redesignr";
import {
  ErrorBoundary,
  GlobalHeader,
  hasMinProfile,
  useDialogContext,
  useUserContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { WSMBikeBellSound } from "@WahooFitness/wsm-native/dist/esm/types/bolt_cfg";
import { Checkmark } from "@carbon/icons-react";
import { t } from "@lingui/macro";
import {
  Box,
  Button,
  Container,
  IconButton,
  LinearProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import useNavUpOnDeviceDisconnect from "../useNavUpOnDeviceDisconnect";
import { useBellSettings } from "./useBellSettings";

// A change event can land a moment after the window regains focus.
const pickerClosedGraceMs = 2000;

// The input lives on document.body, not in the React tree: iOS drops the change event if a
// re-render detaches the input while the system picker is up.
const pickCustomSoundFile = (onPicked: (file: File) => void) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".mp3,.ogg,.wav,audio/mpeg,audio/ogg,audio/wav";
  input.style.display = "none";
  input.setAttribute("data-testid", "settings.device.bikeBell.fileInput");

  let removalTimer: ReturnType<typeof setTimeout> | undefined;
  const remove = () => {
    clearTimeout(removalTimer);
    window.removeEventListener("focus", removeSoon);
    window.removeEventListener("blur", holdRemoval);
    input.remove();
  };
  // Not every WebKit reports a cancelled picker. An open picker takes focus from the window, so
  // a window that stays focused for a moment means the picker is gone.
  const removeSoon = () => {
    clearTimeout(removalTimer);
    removalTimer = setTimeout(remove, pickerClosedGraceMs);
  };
  const holdRemoval = () => clearTimeout(removalTimer);

  input.onchange = () => {
    const file = input.files?.[0];
    remove();
    if (file) onPicked(file);
  };
  input.addEventListener("cancel", remove);
  window.addEventListener("focus", removeSoon);
  window.addEventListener("blur", holdRemoval);
  document.body.appendChild(input);
  input.click();
};

const BellSettings = ({ appToken }: { appToken: string }) => {
  const {
    bellEnabled,
    sound,
    customFile,
    customFiles,
    isDeviceConnected,
    controlsDisabled,
    customFileMissing,
    isPlaying,
    isUploading,
    uploadProgress,
    uploadFileName,
    isDeleting,
    canEditCustomSounds,
    setEnabled,
    selectSound,
    playBell,
    uploadCustomSound,
    deleteCustomSound,
    isLoading,
    error,
    reload,
  } = useBellSettings(appToken);
  const { currentUser } = useUserContext();
  const { isDrawer, close } = useDrawerPresentation("bellSettings");
  const { palette } = useTheme();
  const { setDialog, handleClose } = useDialogContext();
  const [editing, setEditing] = useState(false);
  const hasCustomFiles = customFiles.length > 0;
  const canEdit = canEditCustomSounds && !controlsDisabled;

  useNavUpOnDeviceDisconnect(appToken);

  // A transfer in flight only pauses editing; anything else that rules it out ends it.
  useEffect(() => {
    if (!canEdit && !isUploading && !isDeleting) setEditing(false);
  }, [canEdit, isDeleting, isUploading]);

  const toggleEditing = useCallback(() => setEditing((value) => !value), []);

  const confirmDelete = useCallback(
    (name: string) => {
      setDialog({
        open: true,
        title: t`Delete custom sound?`,
        body: t`${name} will be removed from your ELEMNT.`,
        actions: [
          { text: t`Cancel`, action: handleClose },
          {
            text: t`Delete`,
            color: "error",
            action: () => {
              deleteCustomSound(name);
              handleClose();
            },
          },
        ],
      });
    },
    [deleteCustomSound, handleClose, setDialog]
  );

  const checkmark = useMemo(
    () => (
      <Box>
        <Checkmark size={24} fill={palette.info.main} />
      </Box>
    ),
    [palette.info.main]
  );

  const enabledItems: MenuListItemType[] = useMemo(
    () => [
      {
        id: "bikeBellEnabled",
        icon: <WfIconMiscBikeBellRegular />,
        content: t`Bike bell`,
        variant: MenuListItemVariant.Action,
        actionComponent: <BlueSwitch checked={bellEnabled} />,
        action: () => setEnabled(!bellEnabled),
        disabled: !isDeviceConnected,
        "data-testid": "settings.device.bikeBell.enabled",
      },
    ],
    [bellEnabled, isDeviceConnected, setEnabled]
  );

  const soundItems: MenuListItemType[] = useMemo(() => {
    const builtInSounds = [
      {
        id: "classic",
        sound: WSMBikeBellSound.Classic,
        label: t({ message: "Classic", comment: "Bike bell sound name" }),
      },
      {
        id: "modern",
        sound: WSMBikeBellSound.Modern,
        label: t({ message: "Modern", comment: "Bike bell sound name" }),
      },
      {
        id: "jingle",
        sound: WSMBikeBellSound.Jingle,
        label: t({ message: "Jingle", comment: "Bike bell sound name" }),
      },
    ];
    return builtInSounds.map((builtIn) => ({
      id: `bikeBellSound-${builtIn.id}`,
      content: builtIn.label,
      variant: MenuListItemVariant.Action,
      actionComponent: sound === builtIn.sound ? checkmark : undefined,
      action: () => selectSound(builtIn.sound),
      disabled: controlsDisabled,
      "data-testid": `settings.device.bikeBell.sound.${builtIn.id}`,
    }));
  }, [checkmark, controlsDisabled, selectSound, sound]);

  const customSoundItems: MenuListItemType[] = useMemo(
    () => [
      ...customFiles.map((name) => {
        const selected = sound === WSMBikeBellSound.Custom && customFile === name;
        const deletable = editing && !selected;
        return {
          id: `bikeBellCustom-${name}`,
          // File names come straight from the ELEMNT and are never translated.
          content: <Typography noWrap>{name}</Typography>,
          variant: deletable ? MenuListItemVariant.NoAction : MenuListItemVariant.Action,
          actionComponent: deletable ? (
            <IconButton
              onClick={() => confirmDelete(name)}
              disabled={!canEdit}
              data-testid={`settings.device.bikeBell.delete-${name}`}
            >
              <DeleteIcon color="error" height={24} width={24} />
            </IconButton>
          ) : selected ? (
            checkmark
          ) : undefined,
          action: deletable ? undefined : () => selectSound(WSMBikeBellSound.Custom, name),
          // Only an Action row greys out when disabled; the active file stays one while editing.
          disabled: controlsDisabled || (editing && selected),
        };
      }),
      ...(customFileMissing
        ? [
            {
              id: "bikeBellCustomMissing",
              content: <Typography noWrap>{customFile}</Typography>,
              secondaryContent: t`File not found on ELEMNT`,
              variant: MenuListItemVariant.NoAction,
              actionComponent: checkmark,
              disabled: controlsDisabled,
            },
          ]
        : []),
    ],
    [
      canEdit,
      checkmark,
      confirmDelete,
      controlsDisabled,
      customFile,
      customFileMissing,
      customFiles,
      editing,
      selectSound,
      sound,
    ]
  );

  return (
    <>
      <GlobalHeader
        title={t`Bike bell`}
        primaryLeftActionItem={
          isDrawer ? { icon: <WfIconNavigationCloseRegular />, action: close } : undefined
        }
        backAction={isDrawer ? close : undefined}
      />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <Box height="100%" width="100%" display="flex" flexDirection="column" overflow="hidden">
          <ErrorBoundary
            error={error}
            isLoading={isLoading}
            onReload={reload}
            showError={hasMinProfile(Profile.alpha, currentUser?.app?.profile)}
          >
            <Box display="flex" flexDirection="column" py={2} height="100%" overflow="scroll">
              <Box>
                <MenuList listItems={enabledItems} />
                <Box width="100%" px={2} py={1}>
                  <Button
                    size="large"
                    variant="outlined"
                    fullWidth
                    startIcon={<WfIconMiscPlayRegular />}
                    disabled={controlsDisabled || isPlaying}
                    onClick={playBell}
                    data-testid="settings.device.bikeBell.play"
                  >
                    {t`Play on ELEMNT`}
                  </Button>
                </Box>
              </Box>
              <MenuList title={t`Sound`} listItems={soundItems} />
              <Box>
                {customSoundItems.length > 0 ? (
                  <>
                    {hasCustomFiles && (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        px={2}
                        pt={1}
                      >
                        <Typography variant="prose-sm-medium">{t`Custom sounds`}</Typography>
                        <EditButton
                          editing={editing}
                          toggleEditing={toggleEditing}
                          disabled={!canEdit}
                        />
                      </Box>
                    )}
                    <MenuList
                      title={hasCustomFiles ? undefined : t`Custom sounds`}
                      listItems={customSoundItems}
                    />
                    <Box px={4}>
                      <Typography variant="prose-3xs" color="text.secondary">
                        {t`MP3, WAV, or OGG files under 500KB supported.`}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Box px={2} py={1}>
                    <Typography variant="prose-sm-medium" my={1}>
                      {t`Custom sounds`}
                    </Typography>
                    <Typography variant="prose-3xs" color="text.secondary" display="block">
                      {t`MP3, WAV, or OGG files under 500KB supported.`}
                    </Typography>
                  </Box>
                )}
                <Box width="100%" px={2} py={1}>
                  <Button
                    size="large"
                    variant="contained"
                    fullWidth
                    startIcon={<AddIcon />}
                    disabled={controlsDisabled || isUploading || isDeleting}
                    onClick={() => pickCustomSoundFile(uploadCustomSound)}
                    data-testid="settings.device.bikeBell.addCustom"
                  >
                    {t`Add custom sound`}
                  </Button>
                  {isUploading && (
                    <Box mt={1}>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                      <Typography
                        variant="prose-3xs"
                        color="text.secondary"
                        display="block"
                        mt={0.5}
                        sx={{ overflowWrap: "anywhere" }}
                      >
                        {t`Sending ${uploadFileName} to ELEMNT…`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </ErrorBoundary>
        </Box>
      </Container>
    </>
  );
};

export { BellSettings };
